import { useCallback } from 'react';

import { InquisitiveActivitiesContentType } from 'global/graphql/scalars.type';
import { Box } from 'approot/shared/design-system/box/box.style';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import { SettingSmallText } from '../settings.style';
import useGetLazyActivitiesChanges from './use-get-lazy-activities-changes';
import { isResponseWithAnswersUpdated } from './logic/is-response-with-answers-updated';

type Props = {
  contentId: number;
  contentType: InquisitiveActivitiesContentType;
  disabled: boolean;
  openReviewChangesModal: () => void;
  openPublishMinorUpdateModal: () => void;
  setIsCheckingForChanges: (state: boolean) => void;
};

const SettingsPublishMinor = ({
  contentId,
  contentType,
  disabled,
  openReviewChangesModal,
  openPublishMinorUpdateModal,
  setIsCheckingForChanges,
}: Props) => {
  const { loading, getActivitiesChanges } = useGetLazyActivitiesChanges({
    onError: () => {
      setIsCheckingForChanges(false);
    },
  });

  const onMinorUpdateSelected = useCallback(async () => {
    setIsCheckingForChanges(true);

    const { data } = await getActivitiesChanges({
      fetchPolicy: 'cache-and-network',
      variables: {
        input: {
          contentId,
          contentType,
        },
      },
    });

    if (data?.getActivitiesChanges && data.getActivitiesChanges.length > 0) {
      if (isResponseWithAnswersUpdated(data.getActivitiesChanges)) {
        openReviewChangesModal();
      } else {
        openPublishMinorUpdateModal();
      }
    } else {
      openPublishMinorUpdateModal();
    }
  }, [
    contentId,
    contentType,
    getActivitiesChanges,
    openPublishMinorUpdateModal,
    openReviewChangesModal,
    setIsCheckingForChanges,
  ]);

  return (
    <>
      <Box $marginBottom={8}>
        <SecondaryLoadingButton
          type="button"
          onClick={() => onMinorUpdateSelected()}
          disabled={loading || disabled}
          isLoading={loading}
          data-selector="btn-publish-minor-update"
        >
          Publish Minor Update
        </SecondaryLoadingButton>
      </Box>
      <SettingSmallText>
        For small refinements that don’t change how the lesson is taught. Ideal
        for minor text tweaks or corrections.
      </SettingSmallText>
    </>
  );
};

export default SettingsPublishMinor;
